import React, { useState, useEffect, useRef } from 'react';
import { SearchBox } from '@mapbox/search-js-react';
import { supabase } from '../lib/supabase';
import { Maskot } from './Maskot';

export const AddSchoolModal = React.memo(function AddSchoolModal({ theme, MAPBOX_TOKEN, onClose }) {
  const isDark = theme === 'dark';
  const C = (k) => {
    const d = isDark, m = {
      surf: d ? '#141924' : '#FFFAF5',
      surf2: d ? 'rgba(255,255,255,0.04)' : 'rgba(139,92,246,0.04)',
      bord: d ? 'rgba(255,255,255,0.07)' : 'rgba(139,92,246,0.10)',
      bord2: d ? 'rgba(255,255,255,0.12)' : 'rgba(139,92,246,0.14)',
      txt: d ? '#ffffff' : '#1E1048',
      txt2: d ? 'rgba(255,255,255,0.5)' : '#4B3D7A',
      txt3: d ? 'rgba(255,255,255,0.25)' : '#9B8FBD',
      inp: d ? 'rgba(255,255,255,0.05)' : 'rgba(139,92,246,0.06)',
    };
    return m[k] || '';
  };
  const [form, setForm] = useState({
    name: '', email: '', phone: '', school_name: '', school_address: '',
    city: '', district: '', neighborhood: '',
    school_type: 'anaokul',
  });

  const SCHOOL_TYPES = [
    ['anaokul', '🏫 Anaokul / Kreş'],
    ['oyunevi', '🎠 Oyunevi'],
    ['kurs', '🎨 Kurs / Atölye'],
    ['ozel', '💜 Özel Eğitim'],
    ['spor', '⚽ Spor Okulu'],
    ['muzik', '🎵 Müzik / Dans'],
  ];
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState('');
  // Adres geocoding
  const [suggestions, setSuggestions] = useState([]);
  const [geoLoading, setGeoLoading] = useState(false);
  const [coords, setCoords] = useState(null); // { lat, lng, place_name }
  const [showSuggest, setShowSuggest] = useState(false);
  const debounceRef = useRef(null);
  // İnteraktif harita (draggable pin)
  const mapContainerRef = useRef(null);
  const mapRef = useRef(null);
  const markerRef = useRef(null);

  // coords değişince harita merkezini ve pin'i güncelle
  useEffect(() => {
    if (!coords || !mapContainerRef.current) return;
    const mapboxgl = window.mapboxgl;
    if (!mapboxgl) return;
    mapboxgl.accessToken = MAPBOX_TOKEN;
    if (!mapRef.current) {
      const map = new mapboxgl.Map({
        container: mapContainerRef.current,
        style: isDark ? 'mapbox://styles/mapbox/dark-v11' : 'mapbox://styles/mapbox/streets-v12',
        center: [coords.lng, coords.lat],
        zoom: 15.5,
        attributionControl: false,
      });
      map.addControl(new mapboxgl.NavigationControl({ showCompass: false }), 'top-right');
      mapRef.current = map;
      const marker = new mapboxgl.Marker({ color: '#A8DAFF', draggable: true })
        .setLngLat([coords.lng, coords.lat])
        .addTo(map);
      marker.on('dragend', async () => {
        const { lng, lat } = marker.getLngLat();
        await applyLocation({ lat, lng });
      });
      markerRef.current = marker;
      // Tıklayınca pin'i taşı
      map.on('click', async (e) => {
        const { lng, lat } = e.lngLat;
        marker.setLngLat([lng, lat]);
        await applyLocation({ lat, lng });
      });
    } else {
      mapRef.current.flyTo({ center: [coords.lng, coords.lat], zoom: 15.5, duration: 600 });
      if (markerRef.current) markerRef.current.setLngLat([coords.lng, coords.lat]);
    }
  }, [coords?.lat, coords?.lng]); // eslint-disable-line

  // Modal kapanırken haritayı temizle
  useEffect(() => {
    return () => {
      if (mapRef.current) { mapRef.current.remove(); mapRef.current = null; }
      markerRef.current = null;
    };
  }, []);

  const inp = {
    width: '100%', background: C('inp'), border: '1px solid ' + C('bord2'),
    borderRadius: 10, padding: '11px 14px', color: C('txt'), fontSize: 16,
    outline: 'none', fontFamily: 'DM Sans,sans-serif', boxSizing: 'border-box',
  };
  const lbl = { fontSize: 12, fontWeight: 600, color: C('txt2'), marginBottom: 5, display: 'block', letterSpacing: '0.2px' };

  // Adres autocomplete — Mapbox geocoding (TR bbox + İstanbul proximity)
  const geocode = async (q) => {
    if (!q || !q.trim() || !MAPBOX_TOKEN) { setSuggestions([]); return; }
    setGeoLoading(true);
    try {
      const url = `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(q)}.json?country=TR&language=tr&limit=5&proximity=29.0270,41.0131&bbox=25.6,35.8,45.0,42.4&types=address,poi,place,locality,neighborhood&access_token=${MAPBOX_TOKEN}`;
      const r = await fetch(url);
      const data = await r.json();
      const feats = (data.features || []).map(f => {
        const ctx = f.context || [];
        const get = (type) => ctx.find(c => (c.id || '').startsWith(type))?.text ?? '';
        // TR Mapbox: region=İl (İstanbul), district field nadiren dolu —
        // place TR'de ilçe (Üsküdar) gibi davranır, region'dan farklıysa ilçedir.
        const region = get('region');
        const place = get('place');
        const districtCtx = get('district');
        const districtFinal = districtCtx || (place && place !== region ? place : '');
        return {
          id: f.id,
          place_name: f.place_name,
          lng: f.center?.[0],
          lat: f.center?.[1],
          city: region,
          district: districtFinal,
          neighborhood: get('neighborhood') || get('locality'),
        };
      }).filter(x => x.lng != null && x.lat != null);
      setSuggestions(feats);
    } catch {
      setSuggestions([]);
    } finally {
      setGeoLoading(false);
    }
  };

  const onAddressChange = (e) => {
    const v = e.target.value;
    setForm(f => ({ ...f, school_address: v }));
    setCoords(null);
    setShowSuggest(true);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => geocode(v), 400);
  };

  // Nominatim reverse geocode — TR için il/ilçe/semt
  // Nominatim TR alanları: province=İstanbul, city_district=Üsküdar, suburb="Kısıklı Mahallesi"
  const enrichWithNominatim = async (lat, lng) => {
    try {
      const r = await fetch(`https://nominatim.openstreetmap.org/reverse?lat=${lat}&lon=${lng}&format=json&addressdetails=1&accept-language=tr`);
      const data = await r.json();
      if (!data?.address) return null;
      const a = data.address;
      // TR Nominatim: province=İl (İstanbul). city alanı bazen ilçeye işaret eder.
      const province = a.province || a.state || '';
      const cityRaw = a.city || '';
      // İl: önce province; yoksa city (büyükşehir merkez ise il, değilse ilçedir → aşağıda kullan)
      const cityFinal = province || (cityRaw && cityRaw === a.state ? cityRaw : '') || cityRaw;
      // İlçe: city_district veya county kesin; province ile city farklıysa city aslında ilçedir.
      const districtFinal =
        a.city_district ||
        a.county ||
        a.municipality ||
        (province && cityRaw && cityRaw !== province ? cityRaw : '') ||
        a.town ||
        a.borough ||
        a.state_district ||
        a.district ||
        '';
      const neighborhood = (a.suburb || '').replace(/ Mahallesi$/i, '') || a.neighbourhood || a.quarter || '';
      return {
        city: province || cityFinal,
        district: districtFinal,
        neighborhood,
        place_name: data.display_name || '',
      };
    } catch { return null; }
  };

  const applyLocation = async ({ lat, lng, place_name, mapboxCity, mapboxDistrict, mapboxNeighborhood }) => {
    // Önce Mapbox context'inden gelen değerleri yaz (varsa)
    setForm(f => ({
      ...f,
      ...(place_name ? { school_address: place_name } : {}),
      ...(mapboxCity ? { city: mapboxCity } : {}),
      ...(mapboxDistrict ? { district: mapboxDistrict } : {}),
      ...(mapboxNeighborhood ? { neighborhood: mapboxNeighborhood } : {}),
    }));
    setCoords({ lat, lng, place_name: place_name || '' });
    // Nominatim ile detaylandır
    const enriched = await enrichWithNominatim(lat, lng);
    if (enriched) {
      setForm(f => ({
        ...f,
        ...(enriched.city ? { city: enriched.city } : {}),
        ...(enriched.district ? { district: enriched.district } : {}),
        ...(enriched.neighborhood ? { neighborhood: enriched.neighborhood } : {}),
        ...(!place_name && enriched.place_name ? { school_address: enriched.place_name } : {}),
      }));
    }
  };

  const pickSuggestion = (s) => {
    setSuggestions([]);
    setShowSuggest(false);
    applyLocation({
      lat: s.lat, lng: s.lng, place_name: s.place_name,
      mapboxCity: s.city, mapboxDistrict: s.district, mapboxNeighborhood: s.neighborhood,
    });
  };

  // Telefon formatlayıcı: +90 5XX XXX XX XX
  const formatPhone = (raw) => {
    let d = String(raw || '').replace(/\D/g, '');
    if (d.startsWith('90')) d = d.slice(2);
    else if (d.startsWith('0')) d = d.slice(1);
    d = d.slice(0, 10);
    let out = '+90';
    if (d.length > 0) out += ' ' + d.slice(0, 3);
    if (d.length > 3) out += ' ' + d.slice(3, 6);
    if (d.length > 6) out += ' ' + d.slice(6, 8);
    if (d.length > 8) out += ' ' + d.slice(8, 10);
    return out;
  };
  const onPhoneChange = (e) => {
    const formatted = formatPhone(e.target.value);
    setForm(f => ({ ...f, phone: formatted }));
  };
  const onPhoneFocus = (e) => { if (!form.phone) setForm(f => ({ ...f, phone: '+90 ' })); };
  const onPhoneBlur = (e) => {
    const v = (form.phone || '').trim();
    if (v === '+90' || v === '+90 ') setForm(f => ({ ...f, phone: '' }));
  };

  const submit = async () => {
    setError('');
    if (!form.name.trim() || !form.email.trim() || !form.phone.trim() || !form.school_name.trim() || !form.school_address.trim()) {
      setError('Lütfen tüm alanları doldurun.');
      return;
    }
    if (!coords) {
      setError('Lütfen adres alanına yazıp listeden bir konum seçin (haritada doğrulansın).');
      return;
    }
    const emailTrim = form.email.trim();
    if (!/^\S+@\S+\.\S+$/.test(emailTrim)) {
      setError('Geçerli bir e-posta girin.');
      return;
    }
    const phoneDigits = form.phone.replace(/\D/g, '');
    if (phoneDigits.length < 10) {
      setError('Telefon numarası eksik. Örn: +90 532 213 84 87');
      return;
    }
    const phoneNorm = phoneDigits.startsWith('90') ? phoneDigits : '90' + phoneDigits.replace(/^0/, '');
    setSubmitting(true);
    // Duplicate engelinden muaf olan e-postalar (test/admin)
    const DUP_BYPASS_EMAILS = new Set(['komofti@gmail.com']);
    const bypassDup = DUP_BYPASS_EMAILS.has(emailTrim.toLowerCase());
    try {
      if (!bypassDup) {
        // Duplicate kontrol — aynı email veya telefonla zaten kayıt var mı?
        const { data: existing, error: dupErr } = await supabase
          .from('school_requests')
          .select('id, status, director_email, director_phone')
          .or(`director_email.eq.${emailTrim},director_phone.eq.${form.phone}`)
          .limit(5);
        if (!dupErr && Array.isArray(existing) && existing.length > 0) {
          const blocking = existing.find(r => {
            const sameEmail = (r.director_email || '').toLowerCase() === emailTrim.toLowerCase();
            const samePhone = (r.director_phone || '').replace(/\D/g, '') === phoneDigits;
            return (sameEmail || samePhone) && (r.status !== 'rejected');
          });
          if (blocking) {
            setError('Bu e-posta veya telefon numarası ile zaten bir talebiniz var. Durumunu öğrenmek için bizimle iletişime geçin.');
            setSubmitting(false);
            return;
          }
        }
      }

      // Konum varsa lat/lng ile birlikte kaydet — kolon yoksa fallback
      const insertPayload = {
        school_name: form.school_name.trim(),
        director_name: form.name.trim(),
        director_email: emailTrim,
        director_phone: form.phone,
        city: (form.city || '').trim(),
        district: (form.district || '').trim() || form.school_address.trim(),
        school_type: form.school_type || 'anaokul',
        status: 'pending',
        ...(form.neighborhood ? { neighborhood: form.neighborhood.trim() } : {}),
        ...(form.school_address ? { address: form.school_address.trim() } : {}),
        ...(coords ? { latitude: coords.lat, longitude: coords.lng } : {}),
      };
      let insErr = null;
      const tryInsert = async (p) => {
        const r = await supabase.from('school_requests').insert(p);
        return r?.error || null;
      };
      insErr = await tryInsert(insertPayload);
      // Şema'da olmayan kolonlar için fallback: hangi kolon eksikse onu kaldır, retry
      let attempts = 0;
      while (insErr && /column .* of relation .* does not exist|column ".*?" does not exist|could not find .* column|schema cache/i.test(insErr.message || '') && attempts < 8) {
        // Hangi kolonun eksik olduğunu bul (PostgREST mesajı: "Could not find the 'X' column")
        const m1 = (insErr.message || '').match(/column "?([a-z_]+)"? of relation/i);
        const m2 = (insErr.message || '').match(/column "([a-z_]+)" does not exist/i);
        const m3 = (insErr.message || '').match(/could not find the '([a-z_]+)' column/i);
        const missing = m1?.[1] || m2?.[1] || m3?.[1];
        if (!missing || !(missing in insertPayload)) break;
        console.warn('[AddSchool] missing column, dropping & retry:', missing);
        delete insertPayload[missing];
        attempts++;
        insErr = await tryInsert(insertPayload);
      }
      if (insErr) {
        console.error('[AddSchool] insert error:', insErr);
      }
      const insOk = !insErr;
      if (!insOk) {
        if (insErr.code === '23505' || /duplicate|unique/i.test(insErr.message || '')) {
          throw new Error('Bu e-posta veya telefon numarası ile zaten bir talebiniz var.');
        }
        throw new Error(insErr.message);
      }
      setDone(true);
    } catch (e) {
      setError(e.message || 'Talep gönderilemedi.');
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <div style={{ position: 'fixed', inset: 0, zIndex: 1100, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(0,0,0,0.8)', backdropFilter: 'blur(8px)' }} onClick={onClose}>
        <div style={{ background: C('surf'), borderRadius: 20, padding: 32, width: 420, maxWidth: '94vw', textAlign: 'center', border: '1px solid ' + C('bord2'), boxShadow: '0 24px 64px rgba(0,0,0,0.5)' }} onClick={e => e.stopPropagation()}>
          <Maskot type="mod" name="excited" size={80} style={{ margin: '0 auto 14px' }} />
          <div style={{ color: C('txt'), fontSize: 18, fontWeight: 800, marginBottom: 8, fontFamily: 'Syne,sans-serif' }}>Talebiniz alındı!</div>
          <div style={{ color: C('txt2'), fontSize: 13, lineHeight: 1.6, marginBottom: 18 }}>
            Okulunuz inceleme sürecine alındı. Onaylandıktan sonra <strong style={{color:C('txt')}}>{form.email}</strong> adresine bilgi göndereceğiz.
          </div>
          <button onClick={onClose} style={{ padding: '11px 28px', borderRadius: 10, background: 'linear-gradient(135deg,#A8DAFF,#7BB8F0)', border: 'none', color: '#000', fontSize: 13, fontWeight: 700, cursor: 'pointer', fontFamily: 'DM Sans,sans-serif' }}>
            Tamam
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 1100, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(0,0,0,0.8)', backdropFilter: 'blur(8px)', padding: 16 }} onClick={onClose}>
      <div style={{ background: C('surf'), borderRadius: 20, width: 460, maxWidth: '100%', maxHeight: '92vh', overflowY: 'auto', border: '1px solid ' + C('bord2'), boxShadow: '0 24px 64px rgba(0,0,0,0.5)' }} onClick={e => e.stopPropagation()}>
        <div style={{ padding: '22px 26px 8px', borderBottom: '1px solid ' + C('bord'), display: 'flex', alignItems: 'center', justifyContent: 'space-between', position: 'sticky', top: 0, background: C('surf'), zIndex: 1 }}>
          <div>
            <div style={{ fontSize: 18, fontWeight: 800, color: C('txt'), fontFamily: 'Syne,sans-serif' }}>Okul / Kurum Ekle</div>
            <div style={{ fontSize: 12, color: C('txt2'), marginTop: 2 }}>Kurumunuzu OkulMap'e ekletmek için form doldurun</div>
          </div>
          <button onClick={onClose} style={{ width: 32, height: 32, borderRadius: 8, background: C('surf2'), border: '1px solid ' + C('bord'), color: C('txt2'), cursor: 'pointer', fontSize: 16, lineHeight: 1 }}>✕</button>
        </div>
        <div style={{ padding: '20px 26px 26px' }}>
          <div style={{ marginBottom: 14 }}>
            <label style={lbl}>Ad Soyad <span style={{ color: '#FF6B8A' }}>*</span></label>
            <input style={inp} value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} placeholder="Adınız ve soyadınız" maxLength={120} />
          </div>
          <div style={{ marginBottom: 14 }}>
            <label style={lbl}>E-posta <span style={{ color: '#FF6B8A' }}>*</span></label>
            <input style={inp} type="email" value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} placeholder="ornek@mail.com" maxLength={120} />
          </div>
          <div style={{ marginBottom: 14 }}>
            <label style={lbl}>Telefon <span style={{ color: '#FF6B8A' }}>*</span></label>
            <input style={inp} type="tel" value={form.phone} onChange={onPhoneChange} onFocus={onPhoneFocus} onBlur={onPhoneBlur} placeholder="+90 532 213 84 87" maxLength={20} inputMode="tel" autoComplete="tel" />
          </div>
          <div style={{ marginBottom: 14 }}>
            <label style={lbl}>Okul / Kurum Adı <span style={{ color: '#FF6B8A' }}>*</span></label>
            <input style={inp} value={form.school_name} onChange={e => setForm(f => ({ ...f, school_name: e.target.value }))} placeholder="Örn: Akıllı Adımlar Anaokulu" maxLength={160} />
          </div>
          <div style={{ marginBottom: 14 }}>
            <label style={lbl}>Kurum Türü <span style={{ color: '#FF6B8A' }}>*</span></label>
            <select style={{ ...inp, appearance: 'none', backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='8' viewBox='0 0 12 8'%3E%3Cpath fill='%238b95a8' d='M6 8L0 0h12z'/%3E%3C/svg%3E")`, backgroundRepeat: 'no-repeat', backgroundPosition: 'right 14px center', paddingRight: 36 }}
              value={form.school_type}
              onChange={e => setForm(f => ({ ...f, school_type: e.target.value }))}>
              {SCHOOL_TYPES.map(([val, label]) => (
                <option key={val} value={val}>{label}</option>
              ))}
            </select>
          </div>
          <div style={{ marginBottom: 14, position: 'relative' }}>
            <label style={lbl}>Okul / Kurum Adresi <span style={{ color: '#FF6B8A' }}>*</span></label>
            {/* Mapbox SearchBox — admin paneli ile aynı bileşen */}
            <div className="omap-mb-search">
              <style>{`
                .omap-mb-search input { color: ${C('txt')} !important; caret-color: #A8DAFF !important; }
                .omap-mb-search input::placeholder { color: ${C('txt3')} !important; }
              `}</style>
              <SearchBox
                accessToken={MAPBOX_TOKEN}
                value={form.school_address}
                options={{
                  country: 'tr',
                  language: 'tr',
                  types: 'poi,address,street,neighborhood,locality,district,place',
                  limit: 8,
                  proximity: coords ? `${coords.lng},${coords.lat}` : '29.0270,41.0131',
                }}
                onChange={(v) => setForm(f => ({ ...f, school_address: v }))}
                onRetrieve={(res) => {
                  const f = res?.features?.[0];
                  if (!f) return;
                  const props = f.properties || {};
                  const ctx = props.context || {};
                  const coordsArr = f.geometry?.coordinates || [0, 0];
                  const lng = coordsArr[0], lat = coordsArr[1];
                  const fullAddress = props.full_address || props.place_formatted || props.name || '';
                  const region = ctx.region?.name || '';
                  const district = ctx.district?.name || ctx.locality?.name || ctx.place?.name || '';
                  const neigh = ctx.neighborhood?.name || ctx.locality?.name || '';
                  applyLocation({
                    lat, lng, place_name: fullAddress,
                    mapboxCity: region, mapboxDistrict: district, mapboxNeighborhood: neigh,
                  });
                }}
                placeholder="Okul adı veya adres ara..."
                theme={{
                  variables: {
                    colorBackground: C('surf'),
                    colorBackgroundHover: C('surf2'),
                    colorBackgroundActive: C('surf2'),
                    colorText: C('txt'),
                    colorTextHover: C('txt'),
                    colorPlaceholder: C('txt3'),
                    colorPrimary: '#A8DAFF',
                    border: '1px solid ' + C('bord2'),
                    borderRadius: '10px',
                    boxShadow: '0 8px 24px rgba(0,0,0,0.4)',
                    fontFamily: 'DM Sans, sans-serif',
                  },
                  cssText: `
                    input { color: ${C('txt')} !important; caret-color: #A8DAFF !important; }
                    input::placeholder { color: ${C('txt3')} !important; }
                  `,
                }}
              />
            </div>
            {/* Konum onayı + harita + il/ilçe/semt */}
            {coords && (
              <div style={{ marginTop: 10 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6, fontSize: 12, color: '#4ECDC4', fontWeight: 600 }}>
                  ✓ Konum doğrulandı
                </div>
                <div ref={mapContainerRef} style={{ borderRadius: 10, overflow: 'hidden', border: '1px solid ' + C('bord2'), height: 220, marginBottom: 6, position: 'relative' }}/>
                <div style={{ fontSize: 11, color: C('txt3'), marginBottom: 10, lineHeight: 1.5 }}>
                  💡 Pin'i sürükleyerek veya haritada bir yere tıklayarak konumu tam olarak ayarlayabilirsiniz.
                </div>
                {/* Otomatik doldurulan il/ilçe/semt — kullanıcı düzeltebilir */}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8 }}>
                  <div>
                    <div style={{ fontSize: 10, fontWeight: 600, color: C('txt2'), marginBottom: 4, letterSpacing: '0.3px', textTransform: 'uppercase' }}>İl</div>
                    <input style={{ ...inp, padding: '8px 10px', fontSize: 13 }} value={form.city} onChange={e => setForm(f => ({ ...f, city: e.target.value }))} placeholder="—" maxLength={60}/>
                  </div>
                  <div>
                    <div style={{ fontSize: 10, fontWeight: 600, color: C('txt2'), marginBottom: 4, letterSpacing: '0.3px', textTransform: 'uppercase' }}>İlçe</div>
                    <input style={{ ...inp, padding: '8px 10px', fontSize: 13 }} value={form.district} onChange={e => setForm(f => ({ ...f, district: e.target.value }))} placeholder="—" maxLength={60}/>
                  </div>
                  <div>
                    <div style={{ fontSize: 10, fontWeight: 600, color: C('txt2'), marginBottom: 4, letterSpacing: '0.3px', textTransform: 'uppercase' }}>Semt</div>
                    <input style={{ ...inp, padding: '8px 10px', fontSize: 13 }} value={form.neighborhood} onChange={e => setForm(f => ({ ...f, neighborhood: e.target.value }))} placeholder="—" maxLength={80}/>
                  </div>
                </div>
              </div>
            )}
            {!coords && form.school_address && !geoLoading && suggestions.length === 0 && (
              <div style={{ marginTop: 6, fontSize: 11, color: C('txt3'), lineHeight: 1.5 }}>
                Listeden bir adres seçmelisiniz — yazmaya devam edin veya farklı bir arama deneyin.
              </div>
            )}
          </div>
          {error && (
            <div style={{ padding: '10px 12px', borderRadius: 10, background: 'rgba(255,107,138,0.1)', border: '1px solid rgba(255,107,138,0.3)', color: '#FF6B8A', fontSize: 12, marginBottom: 12 }}>
              ⚠️ {error}
            </div>
          )}
          <button onClick={submit} disabled={submitting} style={{ width: '100%', padding: '13px 18px', borderRadius: 12, background: submitting ? 'rgba(168,218,255,0.3)' : 'linear-gradient(135deg,#A8DAFF,#7BB8F0)', border: 'none', color: '#000', fontSize: 14, fontWeight: 700, cursor: submitting ? 'wait' : 'pointer', fontFamily: 'DM Sans,sans-serif' }}>
            {submitting ? 'Gönderiliyor...' : 'Talebimi Gönder'}
          </button>
          <div style={{ marginTop: 12, fontSize: 11, color: C('txt3'), textAlign: 'center', lineHeight: 1.5 }}>
            Talebiniz onaylandığında size e-posta ile bilgi vereceğiz.
          </div>
        </div>
      </div>
    </div>
  );
});
